import {
  Box,
  Grid,
  Heading,
  Icon,
  Link,
  Text,
  VStack,
} from '@chakra-ui/react';
import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import MarketingPageShell, {
  breadcrumbSchema,
} from '../components/MarketingPageShell';
import { SITE_URL } from '../components/PageMeta';

const DISCORD_URL = 'https://discord.com/invite/MUpH2jqg94';
const SUPPORT_PORTAL_URL = 'https://support.wagerwatchdog.com';
const DOCS_URL = 'https://docs.wagerwatchdog.com';
const USER_GUIDE_URL =
  'https://docs.wagerwatchdog.com/collections/1731134-user-guide';
const APP_URL = 'https://app.wagerwatchdog.com';
const SUPPORT_EMAIL = 'support@wagerwatchdog.com';
const CLAIM_EMAIL = 'claim@wagerwatchdog.com';

const linkProps = {
  color: 'primary.400',
  fontWeight: 'semibold',
  _hover: { color: 'primary.300', textDecoration: 'underline' },
};

const BREADCRUMBS = [
  { name: 'Home', path: '/' },
  { name: 'Contact', path: '/contact' },
];

type Channel = {
  title: string;
  body: ReactNode;
  iconPath: string;
  accent: string;
};

const CHANNELS: Channel[] = [
  {
    title: 'Claim your expert profile',
    body: (
      <>
        Are you a tracked expert?{' '}
        <Link as={RouterLink} to="/claim" {...linkProps}>
          Claim your profile
        </Link>{' '}
        with a short form, or email{' '}
        <Link href={`mailto:${CLAIM_EMAIL}`} {...linkProps}>
          {CLAIM_EMAIL}
        </Link>
        .
      </>
    ),
    iconPath:
      'M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z',
    accent: 'green.300',
  },
  {
    title: 'Customer support',
    body: (
      <>
        Account help, billing questions, and general assistance. Email{' '}
        <Link href={`mailto:${SUPPORT_EMAIL}`} {...linkProps}>
          {SUPPORT_EMAIL}
        </Link>{' '}
        or visit{' '}
        <Link href={SUPPORT_PORTAL_URL} isExternal {...linkProps}>
          support.wagerwatchdog.com
        </Link>
        .
      </>
    ),
    iconPath:
      'M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z',
    accent: 'primary.400',
  },
  {
    title: 'Feedback & feature requests',
    body: (
      <>
        Share ideas and vote on product requests at{' '}
        <Link href={SUPPORT_PORTAL_URL} isExternal {...linkProps}>
          support.wagerwatchdog.com
        </Link>
        —or email{' '}
        <Link href={`mailto:${SUPPORT_EMAIL}`} {...linkProps}>
          {SUPPORT_EMAIL}
        </Link>
        .
      </>
    ),
    iconPath:
      'M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-7 12h-2v-2h2v2zm0-4h-2V6h2v4z',
    accent: 'secondary.400',
  },
  {
    title: 'Discord community',
    body: (
      <>
        Join other WagerWatchdog users for discussion, product updates, and
        community support on{' '}
        <Link href={DISCORD_URL} isExternal {...linkProps}>
          Discord
        </Link>
        .
      </>
    ),
    iconPath:
      'M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z',
    accent: '#5865F2',
  },
  {
    title: 'Help docs',
    body: (
      <>
        How-to guides and product documentation in our{' '}
        <Link href={DOCS_URL} isExternal {...linkProps}>
          Help Center
        </Link>{' '}
        and{' '}
        <Link href={USER_GUIDE_URL} isExternal {...linkProps}>
          User Guide
        </Link>
        .
      </>
    ),
    iconPath:
      'M18 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM6 4h5v8l-2.5-1.5L6 12V4z',
    accent: 'accent.info',
  },
];

type Faq = { q: string; a: ReactNode };

const FAQS: Faq[] = [
  {
    q: 'How do I report a bug or billing issue?',
    a: (
      <>
        Open a ticket at{' '}
        <Link href={SUPPORT_PORTAL_URL} isExternal {...linkProps}>
          support.wagerwatchdog.com
        </Link>{' '}
        or email{' '}
        <Link href={`mailto:${SUPPORT_EMAIL}`} {...linkProps}>
          {SUPPORT_EMAIL}
        </Link>
        . For how-to help and troubleshooting, see our{' '}
        <Link href={DOCS_URL} isExternal {...linkProps}>
          docs
        </Link>
        . For refund eligibility, see our{' '}
        <Link as={RouterLink} to="/refunds" {...linkProps}>
          Refund Policy
        </Link>
        .
      </>
    ),
  },
  {
    q: 'Where should I send feature ideas?',
    a: (
      <>
        Submit requests at{' '}
        <Link href={SUPPORT_PORTAL_URL} isExternal {...linkProps}>
          support.wagerwatchdog.com
        </Link>{' '}
        so the team can track and prioritize them. You can also email{' '}
        <Link href={`mailto:${SUPPORT_EMAIL}`} {...linkProps}>
          {SUPPORT_EMAIL}
        </Link>
        .
      </>
    ),
  },
  {
    q: 'How do I join the community?',
    a: (
      <>
        Join the WagerWatchdog{' '}
        <Link href={DISCORD_URL} isExternal {...linkProps}>
          Discord
        </Link>{' '}
        for discussion, product updates, and peer support. The invite link is on
        this page and in the site footer.
      </>
    ),
  },
  {
    q: 'Who do I contact for privacy or legal questions?',
    a: (
      <>
        Email{' '}
        <Link href="mailto:privacy@wagerwatchdog.com" {...linkProps}>
          privacy@wagerwatchdog.com
        </Link>{' '}
        for privacy requests and{' '}
        <Link href="mailto:legal@wagerwatchdog.com" {...linkProps}>
          legal@wagerwatchdog.com
        </Link>{' '}
        for legal matters. See our{' '}
        <Link as={RouterLink} to="/privacy" {...linkProps}>
          Privacy Policy
        </Link>{' '}
        and{' '}
        <Link as={RouterLink} to="/terms" {...linkProps}>
          Terms of Service
        </Link>{' '}
        for full details.
      </>
    ),
  },
];

const CONTACT_DESCRIPTION =
  'Contact WagerWatchdog support by email or support portal, join our Discord community, and find help docs. We are here for account help, feedback, and product questions.';

const CONTACT_SCHEMA = [
  {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'Contact WagerWatchdog',
    description: CONTACT_DESCRIPTION,
    url: `${SITE_URL}/contact`,
    isPartOf: {
      '@type': 'WebSite',
      name: 'WagerWatchdog',
      url: SITE_URL,
    },
    mainEntity: {
      '@type': 'Organization',
      name: 'IvoryShoulder, LLC',
      alternateName: 'WagerWatchdog',
      url: SITE_URL,
      email: SUPPORT_EMAIL,
      contactPoint: [
        {
          '@type': 'ContactPoint',
          contactType: 'customer support',
          email: SUPPORT_EMAIL,
          url: SUPPORT_PORTAL_URL,
          availableLanguage: ['English'],
        },
        {
          '@type': 'ContactPoint',
          contactType: 'privacy',
          email: 'privacy@wagerwatchdog.com',
          availableLanguage: ['English'],
        },
      ],
      sameAs: [DISCORD_URL, APP_URL, DOCS_URL, SUPPORT_PORTAL_URL],
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'How do I report a bug or billing issue?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: `Open a ticket at ${SUPPORT_PORTAL_URL} or email ${SUPPORT_EMAIL}. For how-to help, see ${DOCS_URL}. For refund eligibility, see the Refund Policy on wagerwatchdog.com.`,
        },
      },
      {
        '@type': 'Question',
        name: 'Where should I send feature ideas?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: `Submit requests at ${SUPPORT_PORTAL_URL} or email ${SUPPORT_EMAIL}.`,
        },
      },
      {
        '@type': 'Question',
        name: 'How do I join the community?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: `Join the WagerWatchdog Discord at ${DISCORD_URL} for discussion, product updates, and peer support.`,
        },
      },
      {
        '@type': 'Question',
        name: 'Who do I contact for privacy or legal questions?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Email privacy@wagerwatchdog.com for privacy requests and legal@wagerwatchdog.com for legal matters. See Privacy Policy and Terms of Service on wagerwatchdog.com.',
        },
      },
    ],
  },
  {
    '@context': 'https://schema.org',
    ...breadcrumbSchema(BREADCRUMBS),
  },
];

/**
 * Contact — support channels for the marketing site.
 * SEO focus keyword: contact WagerWatchdog support
 */
export default function Contact() {
  return (
    <MarketingPageShell
      fullTitle="Contact WagerWatchdog Support | Help, Discord & Docs"
      description={CONTACT_DESCRIPTION}
      schema={CONTACT_SCHEMA}
      breadcrumbs={BREADCRUMBS}
      h1="Contact"
      h1Accent="WagerWatchdog"
      lead="Questions, feedback, or need a hand? Reach support by email or our support portal, join Discord, or dig into the docs—whichever fits how you like to get help."
    >
      <Grid
        templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }}
        gap={{ base: 4, md: 5 }}
      >
        {CHANNELS.map((channel) => (
          <Box
            key={channel.title}
            as="article"
            p={{ base: 6, md: 7 }}
            bg="gray.800"
            borderRadius="16px"
            border="1px solid"
            borderColor="whiteAlpha.100"
            transition="all 0.2s"
            _hover={{
              borderColor: 'primary.500',
              transform: 'translateY(-2px)',
              boxShadow: '0 8px 30px rgba(10, 112, 185, 0.12)',
            }}
          >
            <VStack align="start" spacing={4} h="full">
              <Icon viewBox="0 0 24 24" boxSize={8} color={channel.accent}>
                <path fill="currentColor" d={channel.iconPath} />
              </Icon>
              <Heading as="h2" fontSize="xl" color="white">
                {channel.title}
              </Heading>
              <Text color="gray.400" lineHeight="1.7" flex="1" fontSize="sm">
                {channel.body}
              </Text>
            </VStack>
          </Box>
        ))}
      </Grid>

      <Box>
        <Heading
          as="h2"
          fontSize={{ base: 'xl', md: '2xl' }}
          color="white"
          mb={6}
        >
          Before you write us
        </Heading>
        <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={5}>
          {FAQS.map((item) => (
            <Box
              key={item.q}
              p={6}
              bg="gray.800"
              borderRadius="12px"
              border="1px solid"
              borderColor="whiteAlpha.100"
            >
              <Heading as="h3" fontSize="md" color="white" mb={3}>
                {item.q}
              </Heading>
              <Text color="gray.400" lineHeight="1.7" fontSize="sm">
                {item.a}
              </Text>
            </Box>
          ))}
        </Grid>
      </Box>

      <Box
        p={{ base: 6, md: 8 }}
        borderRadius="16px"
        border="1px solid"
        borderColor="whiteAlpha.100"
        bg="linear-gradient(135deg, rgba(10,112,185,0.12) 0%, rgba(31,41,55,0.9) 55%)"
      >
        <Heading as="h2" fontSize={{ base: 'xl', md: '2xl' }} color="white" mb={3}>
          Privacy &amp; legal
        </Heading>
        <Text color="gray.300" lineHeight="1.8" mb={4}>
          Privacy requests:{' '}
          <Link href="mailto:privacy@wagerwatchdog.com" {...linkProps}>
            privacy@wagerwatchdog.com
          </Link>
          . Legal:{' '}
          <Link href="mailto:legal@wagerwatchdog.com" {...linkProps}>
            legal@wagerwatchdog.com
          </Link>
          .
        </Text>
        <Text color="gray.400" lineHeight="1.8" fontSize="sm">
          Also see our{' '}
          <Link as={RouterLink} to="/privacy" {...linkProps}>
            Privacy Policy
          </Link>
          ,{' '}
          <Link as={RouterLink} to="/terms" {...linkProps}>
            Terms of Service
          </Link>
          ,{' '}
          <Link as={RouterLink} to="/refunds" {...linkProps}>
            Refund Policy
          </Link>
          ,{' '}
          <Link as={RouterLink} to="/about" {...linkProps}>
            About Us
          </Link>
          , and{' '}
          <Link as={RouterLink} to="/responsible-gaming" {...linkProps}>
            Responsible Gaming
          </Link>{' '}
          resources.
        </Text>
      </Box>
    </MarketingPageShell>
  );
}
